import { useCallback } from 'react'

import type { Action, FetchMethod } from '../types'
import { dynamicClient, type ResponseData } from '@components/DynamicActions/hooks/useActionParams'
import { downloadViaNavigation } from '@lib/downloadViaNavigation'
import { emitToast } from '@lib/emitToast'

interface UseActionExecutionProps {
  onSuccess?: (res?: ResponseData) => void
}

/**
 * Handles execution flow for backend actions including external links,
 * CSV downloads via redirect, and invoking `dynamicClient` for API-backed actions.
 *
 * Usage:
 *  const { executeAction } = useActionExecution({ onSuccess })
 */
export const useActionExecution = ({ onSuccess }: UseActionExecutionProps) => {
  const executeAction = useCallback(
    async (action: Action) => {
      if (!action.path) {
        emitToast({
          message: 'Action error',
          details: [{ message: 'No action path provided' }],
        })

        return
      }

      if (
        action.response_content_type === 'text/csv' &&
        action.path?.includes('/rails/active_storage/blobs/redirect/')
      ) {
        downloadViaNavigation(action.path, action.target as '_blank' | '_self')
        emitToast({ message: 'Starting CSV download…', type: 'success' })

        return
      }

      if (action.external) {
        emitToast({
          message: 'Action completed successfully',
          type: 'success',
        })

        const url = new URL(action.path, window.location.origin)
        window.open(url.toString(), action.target || '_blank')

        return
      }

      const res = await dynamicClient(
        (action.method as FetchMethod) || 'POST',
        action.path,
        {},
        action.response_content_type || 'application/json',
      )

      onSuccess?.(res)
    },
    [onSuccess],
  )

  return { executeAction }
}
